import { useCallback, useState } from 'react'
import { z } from 'zod'

export type MaskType =
  | 'cpf'
  | 'cnpj'
  | 'phone'
  | 'cep'
  | 'date'
  | 'credit_card'
  | 'credit_card_validate'
  | 'credit_card_year'
  | 'credit_card_cvv'
  | 'house_number'

interface UseMaskConfig {
  type: MaskType
}

const maskConfigs = {
  cpf: { maxLength: 11, maskedMaxLength: 14 },
  cnpj: { maxLength: 14, maskedMaxLength: 18 },
  phone: { maxLength: 11, maskedMaxLength: 15 },
  cep: { maxLength: 8, maskedMaxLength: 9 },
  date: { maxLength: 8, maskedMaxLength: 10 },
  credit_card: { maxLength: 16, maskedMaxLength: 19 },
  credit_card_validate: { maxLength: 2, maskedMaxLength: 2 },
  credit_card_year: { maxLength: 2, maskedMaxLength: 2 },
  credit_card_cvv: { maxLength: 3, maskedMaxLength: 3 },
  house_number: { maxLength: 2, maskedMaxLength: 2 },
}

const maskFunctions = {
  cpf: (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 11)
    return cleaned
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
  },

  cnpj: (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 14)
    return cleaned
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
  },

  phone: (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 11)
    if (cleaned.length <= 10) {
      return cleaned
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
    }
    return cleaned
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
  },

  cep: (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 8)
    return cleaned.replace(/(\d{5})(\d)/, '$1-$2')
  },

  date: (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 8)
    return cleaned
      .replace(/(\d{2})(\d)/, '$1/$2')
      .replace(/(\d{2})(\d)/, '$1/$2')
  },

  credit_card: (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 16)
    return cleaned
      .replace(/(\d{4})(\d)/, '$1-$2')
      .replace(/(\d{4})-(\d{4})(\d)/, '$1-$2-$3')
      .replace(/(\d{4})-(\d{4})-(\d{4})(\d)/, '$1-$2-$3-$4')
  },

  credit_card_validate: (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 2)
    return cleaned
  },

  credit_card_year: (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 2)
    return cleaned
  },
  credit_card_cvv: (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 3)
    return cleaned
  },
  house_number: (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 2)
    return cleaned
  },
}

// Função para validar cartão de crédito usando algoritmo de Luhn
const validateCreditCard = (cardNumber: string): boolean => {
  const cleaned = cardNumber.replace(/\D/g, '')

  if (cleaned.length !== 16) return false

  let sum = 0
  let shouldDouble = false

  // Percorre o número do cartão de trás para frente
  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = Number.parseInt(cleaned.charAt(i), 10)

    if (shouldDouble) {
      digit *= 2
      if (digit > 9) {
        digit -= 9
      }
    }

    sum += digit
    shouldDouble = !shouldDouble
  }

  return sum % 10 === 0
}

function validateCNPJ(value: string): boolean {
  const cleaned = value.replace(/\D/g, '')
  if (cleaned.length !== 14 || /^(\d)\1{13}$/.test(cleaned)) return false

  let length = cleaned.length - 2
  let numbers = cleaned.substring(0, length)
  const digits = cleaned.substring(length)
  let sum = 0
  let pos = length - 7

  for (let i = length; i >= 1; i--) {
    sum += +numbers.charAt(length - i) * pos--
    if (pos < 2) pos = 9
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
  if (result !== +digits.charAt(0)) return false

  length += 1
  numbers = cleaned.substring(0, length)
  sum = 0
  pos = length - 7

  for (let i = length; i >= 1; i--) {
    sum += +numbers.charAt(length - i) * pos--
    if (pos < 2) pos = 9
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
  return result === +digits.charAt(1)
}

export const maskSchemas = {
  cpf: z
    .string()
    .min(1, 'CPF é obrigatório')
    .refine(value => {
      const cleaned = value.replace(/\D/g, '')
      return cleaned.length === 11
    }, 'CPF deve ter 11 dígitos')
    .refine(value => {
      const cleaned = value.replace(/\D/g, '')
      if (cleaned.length !== 11 || /^(\d)\1{10}$/.test(cleaned)) return false
      let sum = 0
      for (let i = 0; i < 9; i++) sum += +cleaned[i] * (10 - i)
      let rest = (sum * 10) % 11
      rest = rest === 10 ? 0 : rest
      if (rest !== +cleaned[9]) return false
      sum = 0
      for (let i = 0; i < 10; i++) sum += +cleaned[i] * (11 - i)
      rest = (sum * 10) % 11
      rest = rest === 10 ? 0 : rest
      return rest === +cleaned[10]
    }, 'CPF inválido'),

  cnpj: z
    .string()
    .min(1, 'CNPJ é obrigatório')
    .refine(value => {
      const cleaned = value.replace(/\D/g, '')
      return cleaned.length === 14
    }, 'CNPJ deve ter 14 dígitos')
    .refine(value => validateCNPJ(value), 'CNPJ inválido'),

  phone: z
    .string()
    .min(1, 'Telefone é obrigatório')
    .refine(value => {
      const cleaned = value.replace(/\D/g, '')
      return cleaned.length >= 10 && cleaned.length <= 11
    }, 'Telefone deve ter entre 10 e 11 dígitos'),

  cep: z
    .string()
    .min(1, 'CEP é obrigatório')
    .refine(
      value => value.replace(/\D/g, '').length === 8,
      'CEP deve ter 8 dígitos'
    ),

  date: z
    .string()
    .refine(value => {
      if (!value) return true

      const cleaned = value.replace(/\D/g, '')
      if (cleaned.length !== 8) return false

      const day = +cleaned.slice(0, 2)
      const month = +cleaned.slice(2, 4)
      const year = +cleaned.slice(4, 8)

      const currentYear = new Date().getFullYear()
      const currentMonth = new Date().getMonth() + 1
      const currentDay = new Date().getDate()

      if (
        year > currentYear ||
        (year === currentYear && month > currentMonth) ||
        (year === currentYear && month === currentMonth && day > currentDay)
      ) {
        return false
      }

      if (
        day < 1 ||
        day > 31 ||
        month < 1 ||
        month > 12 ||
        year < 1900 ||
        year > 2100
      ) {
        return false
      }

      const date = new Date(year, month - 1, day)
      return (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      )
    }, 'Data inválida')
    .transform(value => {
      if (!value) return value

      const cleaned = value.replace(/\D/g, '')
      if (cleaned.length === 8) {
        const day = cleaned.slice(0, 2)
        const month = cleaned.slice(2, 4)
        const year = cleaned.slice(4, 8)
        return `${year}-${month}-${day}`
      }
      return value
    })
    .optional()
    .nullable(),

  credit_card: z
    .string()
    .min(1, 'Cartão é obrigatório')
    .refine(value => {
      return validateCreditCard(value)
    }, 'Número do cartão inválido'),

  credit_card_validate: z
    .string()
    .min(1, 'Obrigatório')
    .refine(value => {
      const cleaned = value.replace(/\D/g, '')
      return cleaned.length === 2
    }, '2 dígitos')
    .refine(value => {
      return Number(value) <= 12
    }, 'Mês inválido'),

  credit_card_year: z
    .string()
    .min(1, 'Obrigatório')
    .refine(value => {
      const cleaned = value.replace(/\D/g, '')
      return cleaned.length === 2
    }, '2 dígitos')
    .refine(value => {
      const lastDigitsYear = new Date().getUTCFullYear().toString().substring(2)

      return Number(value) >= Number(lastDigitsYear)
    }, 'Ano inválido'),

  credit_card_cvv: z
    .string()
    .min(1, 'Obrigatório')
    .refine(value => {
      const cleaned = value.replace(/\D/g, '')
      return cleaned.length === 3
    }, '3 dígitos'),

  house_number: z
    .string()
    .min(1, 'Obrigatório')
    .refine(value => {
      const cleaned = value.replace(/\D/g, '')
      return cleaned.length === 2
    }, '2 dígitos')
    .refine(value => {
      const lastDigitsYear = new Date().getUTCFullYear().toString().substring(2)

      return Number(value) >= Number(lastDigitsYear)
    }, 'Ano inválido'),
}

const removeMask = (value: string) => value.replace(/[\D]/g, '')

export function useMask({ type }: UseMaskConfig) {
  const [maskedValue, setMaskedValue] = useState('')
  const config = maskConfigs[type]
  const maskFunction = maskFunctions[type]

  const applyMask = useCallback(
    (value: string) => {
      if (!value) return ''
      const cleaned = removeMask(value)
      const limited = cleaned.slice(0, config.maxLength)
      return maskFunction(limited)
    },
    [config.maxLength, maskFunction]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Permite teclas de controle e navegação
      if (
        e.ctrlKey ||
        e.metaKey ||
        [
          'Backspace',
          'Delete',
          'Tab',
          'Enter',
          'ArrowLeft',
          'ArrowRight',
          'Home',
          'End',
        ].includes(e.key)
      ) {
        return
      }

      // Bloqueia caracteres não-numéricos
      if (!/^\d$/.test(e.key)) {
        e.preventDefault()
        return
      }

      // Verifica limite de caracteres apenas para dígitos
      const currentValue = (e.target as HTMLInputElement).value
      const cleanValue = removeMask(currentValue)

      if (cleanValue.length >= config.maxLength) {
        e.preventDefault()
      }
    },
    [config.maxLength]
  )

  const getFieldProps = useCallback(
    (field: any) => ({
      ...field,
      value: maskedValue || applyMask(field.value || ''),
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value
        const masked = applyMask(inputValue)
        const unmasked = removeMask(masked)

        setMaskedValue(masked)

        // Chama o onChange original com o valor sem máscara
        field.onChange({
          ...e,
          target: { ...e.target, value: unmasked },
        })
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        const masked = applyMask(e.target.value)
        setMaskedValue(masked)
        field.onBlur?.(e)
      },
      onKeyDown: handleKeyDown,
      maxLength: config.maskedMaxLength,
    }),
    [maskedValue, applyMask, handleKeyDown, config.maskedMaxLength]
  )

  return {
    maskedValue,
    applyMask,
    removeMask,
    getFieldProps,
    schema: maskSchemas[type],
  }
}
